import { findTextLinks } from './flipbook-text-links';

interface FlipbookLink {
	// position and size in percent of the page box
	left: number;
	top: number;
	width: number;
	height: number;
	url?: string;
	page?: number;
}

interface VisiblePage {
	page: number;
	// position and size in percent of the book
	left: number;
	width: number;
}

const ALLOWED_PROTOCOLS = /^(https?:|mailto:|tel:)/i;

function overlaps(a: FlipbookLink, b: FlipbookLink): boolean {
	return a.left < b.left + b.width && b.left < a.left + a.width && a.top < b.top + b.height && b.top < a.top + a.height;
}

/**
 * pdflipbook.js only draws the pages onto canvases, so links of the PDF are lost.
 * This layer reads the link annotations and the web/e-mail addresses in the text with pdf.js and places transparent anchors above the visible pages.
 * The anchors are positioned in percent inside the book element, so they follow resize, zoom, pan and fullscreen.
 */
export class FlipbookLinkLayer {
	private layer: HTMLElement;
	private cache = new Map<number, Promise<FlipbookLink[]>>();
	private renderId = 0;
	private showTimeout = 0;

	constructor(private wrapper: HTMLElement, private container: HTMLElement, private flipbook: any) {
		this.layer = document.createElement('div');
		this.layer.className = 'c-flipbook__links';

		this.container.addEventListener('flipbook:ready', () => {
			this.flipbook.book.appendChild(this.layer);
			this.render();
		});
		this.container.addEventListener('flipbook:pagechange', () => this.render());
		this.container.addEventListener('flipbook:modechange', () => this.render());

		// runs before the flipbook's own capture listener on its root element
		this.wrapper.addEventListener('pointerdown', (e) => {
			const target = e.target as HTMLElement;
			if (target.closest('.c-flipbook__link')) {
				// a link must not start a page turn, pan or pointer capture, otherwise the click never arrives
				e.stopPropagation();
			} else if (target.closest('.fb-hot, .fb-nav')) {
				// the links of the current spread do not match while a page is turned
				this.hide();
			}
		}, true);

		// a cancelled page turn does not emit a pagechange, so show the links again after the animation
		this.wrapper.addEventListener('pointerup', () => {
			if (this.layer.hidden) {
				window.clearTimeout(this.showTimeout);
				this.showTimeout = window.setTimeout(() => this.render(), 700);
			}
		}, true);
	}

	private hide() {
		window.clearTimeout(this.showTimeout);
		this.layer.hidden = true;
	}

	// promise chains instead of async/await: the babel setup of this project has no regenerator runtime
	private render() {
		if (!this.flipbook.pdf) {
			return;
		}

		const renderId = ++this.renderId;
		const pages = this.getVisiblePages();
		Promise.all(pages.map((o) => this.getLinks(o.page))).then((links) => {
			if (renderId === this.renderId) {
				this.renderPages(pages, links);
			}
		});
	}

	private renderPages(pages: VisiblePage[], links: FlipbookLink[][]) {
		this.layer.innerHTML = '';
		pages.forEach((visiblePage, i) => {
			if (links[i].length === 0) {
				return;
			}
			const box = document.createElement('div');
			box.className = 'c-flipbook__links-page';
			box.style.left = `${visiblePage.left}%`;
			box.style.width = `${visiblePage.width}%`;
			links[i].forEach((link) => box.appendChild(this.createAnchor(link)));
			this.layer.appendChild(box);
		});
		this.layer.hidden = false;
	}

	private getVisiblePages(): VisiblePage[] {
		const flipbook = this.flipbook;
		if (flipbook.mode === 'single') {
			return [{ page: flipbook.viewPage, left: 0, width: 100 }];
		}

		// double mode: "current" sheets are turned, the even page lies left and the odd page right of the spine
		const pages: VisiblePage[] = [];
		const leftPage = flipbook.current * 2;
		const rightPage = leftPage + 1;
		if (leftPage >= 1 && leftPage <= flipbook.numPages) {
			pages.push({ page: leftPage, left: 0, width: 50 });
		}
		if (rightPage <= flipbook.numPages) {
			pages.push({ page: rightPage, left: 50, width: 50 });
		}
		return pages;
	}

	private getLinks(pageNumber: number): Promise<FlipbookLink[]> {
		if (!this.cache.has(pageNumber)) {
			this.cache.set(pageNumber, this.loadLinks(pageNumber).catch(() => []));
		}
		return this.cache.get(pageNumber)!;
	}

	private loadLinks(pageNumber: number): Promise<FlipbookLink[]> {
		return this.flipbook.pdf.getPage(pageNumber).then((page: any) => {
			const viewport = page.getViewport({ scale: 1 });

			// same fitting as pdflipbook.js: the page is scaled into the page box (width 1, height aspect) and centred
			const aspect = this.flipbook.aspect;
			const scale = Math.min(1 / viewport.width, aspect / viewport.height);
			const offsetX = (1 - viewport.width * scale) / 2;
			const offsetY = (aspect - viewport.height * scale) / 2;

			const toLink = (pdfRect: number[], url?: string, page?: number): FlipbookLink => {
				// handles the flipped y-axis and rotated pages of pdf coordinates
				const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(pdfRect);
				const left = offsetX + Math.min(x1, x2) * scale;
				const top = offsetY + Math.min(y1, y2) * scale;
				return {
					left: left * 100,
					top: (top / aspect) * 100,
					width: Math.abs(x2 - x1) * scale * 100,
					height: ((Math.abs(y2 - y1) * scale) / aspect) * 100,
					url,
					page,
				};
			};

			return Promise.all([
				page.getAnnotations({ intent: 'display' }),
				page.getTextContent(),
			]).then(([annotations, textContent]: [any[], any]) => Promise.all(
				annotations
					.filter((o) => o.subtype === 'Link')
					.map((annotation) => {
						const url = annotation.url && ALLOWED_PROTOCOLS.test(annotation.url) ? annotation.url : undefined;
						const targetPage = url || !annotation.dest ? Promise.resolve(undefined) : this.resolveDestination(annotation.dest);
						return targetPage.then((page) => (url || page ? toLink(annotation.rect, url, page) : null));
					})
			).then((annotationLinks) => {
				const links = annotationLinks.filter((o): o is FlipbookLink => o !== null);

				// most pdfs from a layout program have no link annotations, the addresses are plain text
				findTextLinks(textContent.items).forEach((textLink) => {
					const link = toLink(textLink.rect, textLink.url);
					if (links.some((o) => overlaps(o, link)) === false) {
						links.push(link);
					}
				});

				return links;
			}));
		});
	}

	private resolveDestination(dest: string | any[]): Promise<number | undefined> {
		const pdf = this.flipbook.pdf;
		const explicitDest: Promise<any[] | null> = typeof dest === 'string' ? pdf.getDestination(dest) : Promise.resolve(dest);
		return explicitDest
			.then((destination) => {
				const ref = destination?.[0];
				if (ref == null) {
					return undefined;
				}
				return (typeof ref === 'number' ? Promise.resolve(ref) : pdf.getPageIndex(ref)).then((index: number) => index + 1);
			})
			.catch(() => undefined);
	}

	private createAnchor(link: FlipbookLink): HTMLAnchorElement {
		const anchor = document.createElement('a');
		anchor.className = 'c-flipbook__link';
		anchor.style.left = `${link.left}%`;
		anchor.style.top = `${link.top}%`;
		anchor.style.width = `${link.width}%`;
		anchor.style.height = `${link.height}%`;

		if (link.url) {
			anchor.href = link.url;
			anchor.title = link.url;
			if (/^https?:/i.test(link.url)) {
				anchor.target = '_blank';
				anchor.rel = 'noopener noreferrer';
			}
		} else {
			const page = link.page!;
			anchor.href = '#';
			anchor.setAttribute('aria-label', `${page}`);
			anchor.addEventListener('click', (e) => {
				e.preventDefault();
				this.flipbook.goTo(page);
			});
		}
		return anchor;
	}
}
